import { useState, useEffect } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  Bot,
  Clock,
  ArrowRight,
  CheckCircle2,
  Search,
  FileText,
  Quote,
} from "lucide-react";
import LandingNav from "@/components/landing/LandingNav";
import LandingFooter from "@/components/landing/LandingFooter";
import LandingHero from "@/components/landing/LandingHero";
import LandingFAQ from "@/components/landing/LandingFAQ";
import EmailGateModal from "@/components/EmailGateModal";

const CANONICAL = "https://unrankedagency.com/tools/ai-visibility-checker";

const faqs = [
  {
    q: "What is GEO (Generative Engine Optimization)?",
    a: "GEO, or Generative Engine Optimization, is the practice of optimizing your website so it gets retrieved, understood, and cited by AI-powered search tools like ChatGPT, Google AI Overviews, Gemini, and Perplexity. It complements traditional SEO by focusing on how generative engines pick sources rather than how they rank blue links.",
  },
  {
    q: "How do I check if my site is visible in AI search?",
    a: "Run our free AI visibility checker. Paste your URL and we analyze 20 GEO signals across retrieval friendliness (llms.txt, robots.txt for AI bots, semantic HTML), knowledge structuring (schema, FAQ blocks, scannable structure), and citation likelihood (direct-answer paragraphs, original data, brand authority signals).",
  },
  {
    q: "How do I show up in ChatGPT, Gemini, or AI Overviews?",
    a: "AI engines cite content that is easy to retrieve, easy to parse, and easy to quote. In practice that means: allow AI crawlers in robots.txt, ship clean semantic HTML with anchored headings, add Organization and FAQPage schema, lead sections with a direct-answer paragraph, and back claims with original data or named sources.",
  },
  {
    q: "What is the difference between SEO and GEO?",
    a: "Traditional SEO optimizes for blue-link rankings on Google and Bing. GEO optimizes for being retrieved, summarized, and cited by generative AI engines. The two overlap on fundamentals (crawlability, structure, authority) but GEO adds requirements like llms.txt, AI-bot access, and quotable answer-shaped content.",
  },
  {
    q: "Does llms.txt actually help with AI visibility?",
    a: "llms.txt is an emerging standard that gives LLMs a curated map of your most important content. While not yet universally honored, leading AI tools are starting to read it. Publishing one is a low-cost, high-signal step that our AI visibility checker explicitly verifies.",
  },
  {
    q: "Which AI bots should I allow in robots.txt?",
    a: "To be discoverable in generative search you generally want to allow GPTBot (OpenAI / ChatGPT), ClaudeBot (Anthropic), PerplexityBot, Google-Extended (Gemini & AI Overviews), and CCBot (Common Crawl, used by many models). Our checker reads your robots.txt and flags any AI bot that is currently blocked.",
  },
  {
    q: "How long does the AI visibility check take?",
    a: "Most GEO audits complete in under 2 minutes. We crawl your site, run programmatic checks for AI-specific signals, and generate a prioritized action plan you can apply the same day.",
  },
  {
    q: "Is the GEO audit really free?",
    a: "Yes. The AI visibility checker is 100% free, no credit card required. You can run one full audit per email and view the complete report with prioritized recommendations.",
  },
];

const trust = [
  { icon: Sparkles, text: "AI-powered GEO analysis" },
  { icon: Bot, text: "Free — no signup needed" },
  { icon: Clock, text: "Full report in under 2 minutes" },
];

const dimensions = [
  {
    icon: Search,
    title: "Retrieval Friendliness",
    desc: "Can AI engines actually fetch and read your content? We check llms.txt, AI-bot directives in robots.txt, server response health, and semantic HTML structure.",
    items: [
      "llms.txt & llms-full.txt detection",
      "GPTBot, ClaudeBot, PerplexityBot, Google-Extended access",
      "Semantic HTML & clean rendering",
      "Crawl health & status codes",
    ],
  },
  {
    icon: FileText,
    title: "Knowledge Structuring",
    desc: "Is your content organized so an LLM can summarize it confidently? We evaluate schema markup, scannable structure, anchored headings, and FAQ blocks.",
    items: [
      "Organization, Person & FAQPage JSON-LD",
      "Scannable lists & headings",
      "Anchor IDs on key sections",
      "Topical clustering & internal links",
    ],
  },
  {
    icon: Quote,
    title: "Citation Likelihood",
    desc: "Will an AI quote you? We score direct-answer paragraphs, original data, named expertise, freshness, and external authority signals.",
    items: [
      "Direct-answer first paragraphs",
      "Original data, stats & named sources",
      "Author / E-E-A-T signals",
      "Brand mentions & external authority",
    ],
  },
];

const AIVisibilityCheckerPage = () => {
  const [gateUrl, setGateUrl] = useState("");
  const [showGate, setShowGate] = useState(false);

  usePageMeta({
    title:
      "Free AI Visibility Checker — GEO Audit for ChatGPT, Gemini & AI Overviews | Unranked",
    description:
      "Is your site visible in AI? Run a free GEO audit to see how to show up in ChatGPT, Gemini, Perplexity & Google AI Overviews — full report in under 2 minutes.",
    canonical: CANONICAL,
  });

  useEffect(() => {
    const jsonLd = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "SoftwareApplication",
          "@id": `${CANONICAL}#app`,
          name: "Unranked AI Visibility Checker",
          applicationCategory: "SEO Tool",
          operatingSystem: "Web",
          description:
            "Free Generative Engine Optimization (GEO) audit that checks if your site is visible in ChatGPT, Gemini, Perplexity, and Google AI Overviews across 20 AI-specific signals.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          url: CANONICAL,
          creator: { "@id": "https://unrankedagency.com/#organization" },
        },
        {
          "@type": "FAQPage",
          "@id": `${CANONICAL}#faq`,
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${CANONICAL}#breadcrumbs`,
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://unrankedagency.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Tools",
              item: "https://unrankedagency.com/tools/free-seo-audit",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "AI Visibility Checker",
              item: CANONICAL,
            },
          ],
        },
      ],
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleAnalyze = (url: string) => {
    setGateUrl(url);
    setShowGate(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <LandingNav />

      <main>
        <LandingHero
          badge="AI Visibility Checker — Free GEO Audit"
          title={
            <>
              Is Your Site Visible in AI?{" "}
              <span className="gradient-text">Free GEO &amp; AI Visibility</span>{" "}
              Checker
            </>
          }
          subtitle="See exactly how to show up in ChatGPT, Gemini, Perplexity, and Google AI Overviews. Paste your URL for a free Generative Engine Optimization audit — no signup required."
          showUrlInput
          onAnalyze={handleAnalyze}
          ctaLabel="Check My AI Visibility"
          trustItems={trust}
        />

        {/* What is GEO */}
        <section
          className="container mx-auto px-6 py-20"
          aria-labelledby="what-is-geo"
        >
          <div className="max-w-3xl mx-auto text-center">
            <h2
              id="what-is-geo"
              className="text-3xl md:text-4xl font-bold mb-5"
            >
              What is GEO (Generative Engine Optimization)?
            </h2>
            <p className="text-muted-foreground leading-relaxed text-lg">
              GEO is the practice of optimizing a website so it gets{" "}
              <span className="text-foreground">retrieved, understood, and cited</span>{" "}
              by AI search engines like ChatGPT, Gemini, Perplexity, and Google
              AI Overviews. Traditional SEO targets blue-link rankings — GEO
              targets the moment an LLM decides which sources are worth
              quoting. Both share fundamentals, but GEO adds new requirements:
              llms.txt, AI-bot access, schema, and answer-shaped content.
            </p>
          </div>
        </section>

        {/* What we analyze */}
        <section
          className="container mx-auto px-6 py-20"
          aria-labelledby="what-we-analyze"
        >
          <div className="text-center mb-12">
            <h2
              id="what-we-analyze"
              className="text-3xl md:text-4xl font-bold mb-3"
            >
              What the AI Visibility Checker Analyzes
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              20 GEO signals across the three dimensions that decide whether
              an AI engine will surface your site.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {dimensions.map((d) => (
              <article
                key={d.title}
                className="p-6 rounded-xl bg-card border border-border hover:border-primary/30 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <d.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2 text-lg">
                  {d.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {d.desc}
                </p>
                <ul className="space-y-2">
                  {d.items.map((it) => (
                    <li
                      key={it}
                      className="flex items-start gap-2 text-sm text-foreground/90"
                    >
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section
          className="container mx-auto px-6 py-20"
          aria-labelledby="how-to-show-up"
        >
          <div className="text-center mb-14">
            <h2
              id="how-to-show-up"
              className="text-3xl md:text-4xl font-bold mb-3"
            >
              How to Show Up in AI Search — in 3 Steps
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              From a URL to a prioritized GEO action plan in under two minutes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                step: 1,
                title: "Paste Your URL",
                desc: "Drop in any public website — no install, no credit card, no signup.",
              },
              {
                step: 2,
                title: "We Analyze GEO Signals",
                desc: "We programmatically detect llms.txt, AI-bot rules, schema, and structure, then evaluate citation-readiness with AI.",
              },
              {
                step: 3,
                title: "Get Your Action Plan",
                desc: "Receive a prioritized list of fixes — sorted by impact — to make your site visible in ChatGPT, Gemini, and AI Overviews.",
              },
            ].map((s) => (
              <div key={s.step} className="text-center space-y-4">
                <div className="mx-auto w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold">
                  {s.step}
                </div>
                <h3 className="font-semibold text-foreground text-lg">
                  {s.title}
                </h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why AI visibility matters */}
        <section
          className="container mx-auto px-6 py-20"
          aria-labelledby="why-ai-visibility"
        >
          <div className="text-center mb-14">
            <h2
              id="why-ai-visibility"
              className="text-3xl md:text-4xl font-bold mb-3"
            >
              Why AI Visibility Matters Now
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              A fast-growing share of buyers research with AI before they ever
              click a search result. If you are not cited, you are invisible.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              "See if ChatGPT, Gemini & Perplexity can cite you today",
              "Catch blocked AI bots in robots.txt before they cost you traffic",
              "Find missing schema, FAQs and anchored headings LLMs rely on",
              "Get a prioritized GEO action plan, not a generic checklist",
              "Track AI visibility alongside traditional SEO in one report",
              "100% free, no signup, results in under 2 minutes",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p className="text-foreground">{item}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              size="lg"
              className="h-14 px-10 rounded-xl text-base font-semibold glow-primary"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              Run Your Free AI Visibility Check
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </section>

        {/* Related */}
        <section className="container mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              Related Resources
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
            <Button variant="outline" asChild>
              <Link to="/tools/free-seo-audit">Free SEO Audit Tool</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/">← Back to Homepage</Link>
            </Button>
          </div>
        </section>

        <LandingFAQ
          title="AI Visibility & GEO — FAQ"
          subtitle="Common questions about Generative Engine Optimization and AI search visibility."
          faqs={faqs}
        />
      </main>

      <LandingFooter />

      <EmailGateModal
        open={showGate}
        onOpenChange={setShowGate}
        url={gateUrl}
      />
    </div>
  );
};

export default AIVisibilityCheckerPage;
